import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../common/CCUtils"
import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { BackgroundSprite } from "../../generic-sprites/BackgroundSprite"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { SmokeSprite } from "../../generic-sprites/SmokeSprite"
import { VapeSprite } from "../../generic-sprites/VapeSprite"
import { ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { HeroAssets, loadAssets } from "./HeroAssets"
import { HeroPos } from "./HeroPos"
import { HeroStore } from "./HeroStore"
import { Trashable } from "./sprites/Trashable"

export class HeroState extends Phaser.State {

    private static readonly TENS_OF_SECONDS_COUNTDOWN = 6
    private static readonly TRASHABLES_PER_GAME = 40

    blockers: Phaser.Group
    trashGroup: Phaser.Group
    trashables: Phaser.Group
    smoke: Phaser.Group
    clouds: Phaser.Group
    trash: Phaser.Sprite
    coughs: Array<Phaser.Sound> = []
    coughLoop: Phaser.TimerEvent

    trashedTrashables = 0
    trashablesCount: Phaser.Text

    preload() {
        loadAssets(this.game)
        loadGenericAssets(this.game)
    }

    async create() {
        if (!PhaserUtils.loggedIn()) {
            PhaserUtils.notLoggedInMessage(this.game)
            return
        }
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorGray(this.game)

        this.trashedTrashables = 0

        this.coughs[0] = this.game.add.sound(HeroAssets.COUGH_1)
        this.coughs[1] = this.game.add.sound(HeroAssets.COUGH_3)
        this.coughs[2] = this.game.add.sound(HeroAssets.COUGH_4)
        this.coughs[3] = this.game.add.sound(HeroAssets.COUGH_5)
        this.coughs[4] = this.game.add.sound(HeroAssets.COUGH_6)

        // All the basic parts of the game.
        const background = this.game.add.group()
        this.blockers = this.game.add.group()
        this.smoke = this.game.add.group()
        this.clouds = this.game.add.group()
        this.trashGroup = this.game.add.group()
        this.trashables = this.game.add.group()

        const room = new BackgroundSprite(this.game, HeroAssets.BACKGROUND)
        background.add(room)

        const vapeSmokeScale = 0.5
        const smokeScale = 0.3

        const group5 = new Phaser.Sprite(this.game, HeroPos.group5X(), HeroPos.group5Y(),
            HeroAssets.GROUP_5)
        this.blockers.add(group5)
        const group5Smoke = new VapeSprite(this.game, HeroPos.group5SmokeX(), HeroPos.group5SmokeY(), false, vapeSmokeScale,
            this.smoke)
        group5Smoke.vapeLoop(3000)
        const group1 = new Phaser.Sprite(this.game, HeroPos.group1X(), HeroPos.group1Y(),
            HeroAssets.GROUP_1)
        this.blockers.add(group1)
        const group1Smoke = new VapeSprite(this.game, HeroPos.group1SmokeX(), HeroPos.group1SmokeY(), true, vapeSmokeScale,
            this.smoke)
        group1Smoke.vapeLoop(2900)
        const group1B = new Phaser.Sprite(this.game, HeroPos.group1BX(), HeroPos.group1Y(),
            HeroAssets.GROUP_1_B)
        this.blockers.add(group1B)
        const group1BSmoke = new VapeSprite(this.game, HeroPos.group1BSmokeX(), HeroPos.group1BSmokeY(), false,
            vapeSmokeScale, this.smoke)
        group1BSmoke.vapeLoop(3100)
        const group2 = new Phaser.Sprite(this.game, HeroPos.group2X(), HeroPos.group2Y(),
            HeroAssets.GROUP_2)
        this.blockers.add(group2)
        const group2Smoke = new SmokeSprite(this.game, HeroPos.group2SmokeX(), HeroPos.group2SmokeY(), smokeScale,
            this.smoke)
        group2Smoke.randomDelayedSmoke()
        const group3 = new Phaser.Sprite(this.game, HeroPos.group3X(), HeroPos.group3Y(),
            HeroAssets.GROUP_3)
        this.blockers.add(group3)
        const group3Smoke = new VapeSprite(this.game, HeroPos.group3SmokeX(), HeroPos.group3SmokeY(), true, vapeSmokeScale,
            this.smoke)
        group3Smoke.vapeLoop(3200)
        const group4 = new Phaser.Sprite(this.game, HeroPos.group4X(), HeroPos.group4Y(),
            HeroAssets.GROUP_4)
        this.blockers.add(group4)
        const group4Smoke = new SmokeSprite(this.game, HeroPos.group4SmokeX(), HeroPos.group4SmokeY(), smokeScale,
            this.smoke)
        group4Smoke.randomDelayedSmoke()

        const kid = new Phaser.Sprite(this.game, HeroPos.kidX(), HeroPos.kidY(), HeroAssets.KID_SITTING)
        this.blockers.add(kid)
        const baby = new Phaser.Sprite(this.game, HeroPos.babyX(), HeroPos.babyY(), HeroAssets.BABY)
        this.blockers.add(baby)
        const twoKids = new Phaser.Sprite(this.game, HeroPos.twoKidsX(), HeroPos.twoKidsY(),
            HeroAssets.BOY_AND_GIRL)
        this.blockers.add(twoKids)
        const dog = new Phaser.Sprite(this.game, HeroPos.dogX(), HeroPos.dogY(), HeroAssets.DOG)
        this.blockers.add(dog)
        const woman = new Phaser.Sprite(this.game, HeroPos.womanX(), HeroPos.womanY(),
            HeroAssets.WOMAN_SMOKING)
        this.blockers.add(woman)
        const womanSmoke = new SmokeSprite(this.game, HeroPos.womanSmokeX(), HeroPos.womanSmokeY(), smokeScale, this.smoke)
        womanSmoke.randomDelayedSmoke()

        const ashtray = new Phaser.Sprite(this.game, HeroPos.ashtrayX(), HeroPos.ashtrayY(), HeroAssets.ASHTRAY)
        this.blockers.add(ashtray)
        const ashtraySmoke = new SmokeSprite(this.game, HeroPos.ashtraySmokeX(), HeroPos.ashtraySmokeY(), smokeScale,
            this.smoke)
        ashtraySmoke.randomDelayedSmoke()
        const ashtray2 = new Phaser.Sprite(this.game, HeroPos.group2AshtrayX(), HeroPos.group2AshTrayY(), HeroAssets.ASHTRAY)
        this.blockers.add(ashtray2)
        const ashtray2Smoke = new SmokeSprite(this.game, HeroPos.group2AshtraySmokeX(), HeroPos.group2AshTraySmokeY(),
            smokeScale, this.smoke)
        ashtray2Smoke.randomDelayedSmoke()

        this.blockers.forEach((blocker) => {
            Scale.scaleSprite(blocker, 0.5)
        }, this)

        const trash = this.trashGroup.create(HeroPos.trashcanX(), HeroPos.trashcanY(), HeroAssets.TRASHCAN)
        Scale.scaleSprite(trash, 0.58)
        this.trash = trash

        this.trashablesCount = PhaserText.create(this.game, "0",
            {x: HeroPos.trashcanTextX(), y: HeroPos.trashcanTextY(), color: COLOR.white, group: this.trashGroup})

        const appearanceTimes: Array<number> = []
        const millisInGame = HeroState.TENS_OF_SECONDS_COUNTDOWN * 10000
        const millisIncrement = millisInGame / HeroState.TRASHABLES_PER_GAME
        for (let x = 1; x < HeroState.TRASHABLES_PER_GAME + 1; x++) {
            appearanceTimes.push(millisIncrement * x)
        }
        CCUtils.shuffle(appearanceTimes)

        new Trashable(this, HeroPos.ashtrayCigX(), HeroPos.ashtrayCigY(), HeroAssets.CIG,
            0.75, this.trashables, ashtraySmoke, ashtray, appearanceTimes, 1, 13)

        new Trashable(this, HeroPos.group2AshtrayCigX(), HeroPos.group2AshTrayCigY(), HeroAssets.CIG,
            0.75, this.trashables, ashtray2Smoke, ashtray2, appearanceTimes, 13, 26)

        new Trashable(this, HeroPos.womanCigX(), HeroPos.womanCigY(), HeroAssets.CIG,
            0.75, this.trashables, womanSmoke, woman, appearanceTimes, 26, 39)

        new Trashable(this, HeroPos.group5CigX(), HeroPos.group5CigY(), HeroAssets.E_CIG,
            0.75, this.trashables, group5Smoke, group5, appearanceTimes, 39, 52)

        new Trashable(this, HeroPos.group1CigX(), HeroPos.group1CigY(), HeroAssets.E_CIG,
            0.75, this.trashables, group1Smoke, group1, appearanceTimes, 52, 57)

        new Trashable(this, HeroPos.group1BCigX(), HeroPos.group1BCigY(), HeroAssets.E_CIG,
            0.75, this.trashables, group1BSmoke, group1B, appearanceTimes, 57, 62)

        new Trashable(this, HeroPos.group2CigX(), HeroPos.group2CigY(), HeroAssets.CIG,
            0.75, this.trashables, group2Smoke, group2, appearanceTimes, 62, 75)

        new Trashable(this, HeroPos.group3CigX(), HeroPos.group3CigY(), HeroAssets.E_CIG,
            0.75, this.trashables, group3Smoke, group3, appearanceTimes, 75, 88)

        new Trashable(this, HeroPos.group4CigX(), HeroPos.group4CigY(), HeroAssets.CIG,
            0.75, this.trashables, group4Smoke, group4, appearanceTimes, 88, 100)

        const coughTimer = this.game.time.create()
        this.coughLoop = coughTimer.loop(3000, () => {
            this.coughs[CCUtils.randomIndex(this.coughs)].play()
        })

        this.setupCountdown()
    }

    setupCountdown = () => {
        const countdown = this.game.add.image(0, 0, HeroAssets.COUNTDOWN)
        Scale.scaleSprite(countdown, 0.5)
        Sprites.centerInWorldXOnly(countdown)

        const timeRemaining = PhaserText.create(this.game, "TIME REMAINING",
            {y: HeroPos.timeRemainingY(), fontPx: 20, color: COLOR.white})
        Sprites.centerInWorldXOnly(timeRemaining)

        PhaserText.create(this.game, "SECONDS",
            {x: HeroPos.secondsX(), y: HeroPos.secondsY(), fontPx: 17, color: COLOR.white})

        let tensOfSeconds = HeroState.TENS_OF_SECONDS_COUNTDOWN
        let seconds = 0
        let totalSeconds = HeroState.TENS_OF_SECONDS_COUNTDOWN * 10
        const countdownTimer = this.game.time.create()

        const secondsSize = 40
        const tensOfSecondsText = PhaserText.create(this.game, "6",
            {x: HeroPos.timeTensOfSecondsX(), y: HeroPos.timeSecondsY(), fontPx: secondsSize, color: COLOR.white})
        const secondsText = PhaserText.create(this.game, "0",
            {x: HeroPos.timeSecondsX(), y: HeroPos.timeSecondsY(), fontPx: secondsSize, color: COLOR.white})

        countdownTimer.loop(1000, () => {
            totalSeconds--
            if (seconds === 0) {
                seconds = 9
                tensOfSeconds--
            } else {
                seconds--
            }
            tensOfSecondsText.text = tensOfSeconds.toString()
            secondsText.text = seconds.toString()
            if (seconds === 0 && tensOfSeconds === 0) {
                this.gameOver()
                countdownTimer.destroy()
            }
            this.addSmokeEffects(totalSeconds)
        })

        countdownTimer.start(1000)
    }

    gameOver = () => {
        HeroStore.incrementTimesPlayed()
        const score = this.trashedTrashables * 10
        const timesPlayed = HeroStore.getTimesPlayed()
        log.info("Player has now played " + timesPlayed + " times.\n" +
            "trashed " + this.trashedTrashables + " trashables.\n" +
            "score is " + score)
        HeroStore.setScore(score, timesPlayed)
        this.game.state.start(ActivityKey.SUPER_HERO)
    }

    addSmokeEffects = (secondsLeft: number) => {
        let coughFrequency: number
        let smokeAsset: string
        if (secondsLeft === 45 && this.trashedTrashables < 3) {
            coughFrequency = 3000
            smokeAsset = HeroAssets.ROOM_SMOKE
        } else if (secondsLeft === 30 && this.trashedTrashables < 9) {
            coughFrequency = 2000
            smokeAsset = HeroAssets.ROOM_SMOKE_2
        } else if (secondsLeft === 15 && this.trashedTrashables < 15) {
            this.coughs.push(this.game.add.sound(HeroAssets.CRY_1))
            coughFrequency = 1500
            smokeAsset = HeroAssets.ROOM_SMOKE_3
        }

        if (coughFrequency && smokeAsset) {
            log.debug("Adding smoke effects at time: " + secondsLeft)
            this.coughLoop.delay = coughFrequency
            this.coughLoop.timer.start()

            const cloud = this.clouds.create(0, 0, smokeAsset)
            Scale.fillScreenWidth(cloud)
            cloud.y = 0
            cloud.alpha = 0
            Sprites.fade(cloud)
        }
    }

}
